import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash'
import toast from 'react-hot-toast'
import { useIntl } from 'react-intl'
import Modal from '../../Unknown/Modal'
import ConfirmActionOtpModal from '../ConfirmActionOtpModal'
import translate from '../../../i18n/translate'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { eventApi } from '../../../modules/services/eventService'

type EventTypeRow = {
  id: string
  value: string
  title: { en: string; he: string }
  showInDashboard: boolean
  sortOrder: number
  dashboardOrder: number | null
  active: boolean
  isNew?: boolean
}

type EventTypesTab = 'all' | 'dashboard' | 'deleted'

function extractErrorMessage(res: any): string | null {
  const data = res?.data
  if (data?.message?.message) return String(data.message.message)
  if (typeof data?.message === 'string') return data.message
  const errData = res?.error?.data
  if (errData?.message?.message) return String(errData.message.message)
  if (typeof errData?.message === 'string') return errData.message
  return null
}

// Deleted types keep their stored order; only the active ones are renumbered.
function normalizeSortOrder(items: EventTypeRow[]): EventTypeRow[] {
  const sorted = items.filter((x) => x.active).sort((a, b) => a.sortOrder - b.sortOrder)
  const orderMap = new Map<string, number>()
  sorted.forEach((item, index) => orderMap.set(item.id, index))

  return items.map((x) => (orderMap.has(x.id) ? { ...x, sortOrder: orderMap.get(x.id) as number } : x))
}

function normalizeDashboardOrder(items: EventTypeRow[]): EventTypeRow[] {
  const dashItems = items
    .filter((x) => x.showInDashboard)
    .sort((a, b) => (a.dashboardOrder ?? Number.MAX_SAFE_INTEGER) - (b.dashboardOrder ?? Number.MAX_SAFE_INTEGER))

  const orderMap = new Map<string, number>()
  dashItems.forEach((item, index) => orderMap.set(item.id, index))

  return items.map((x) => ({
    ...x,
    dashboardOrder: orderMap.has(x.id) ? (orderMap.get(x.id) as number) : null,
  }))
}

function hydrateInitial(list: Array<Partial<EventTypeRow>>): EventTypeRow[] {
  const hydrated = list.map((item, index) => {
    const active = item.active !== false

    return {
      id: String(item.id),
      value: item.value || '',
      title: {
        en: item.title?.en || '',
        he: item.title?.he || '',
      },
      showInDashboard: active && Boolean(item.showInDashboard),
      sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : index,
      dashboardOrder: typeof item.dashboardOrder === 'number' ? item.dashboardOrder : null,
      active,
    }
  })

  return normalizeDashboardOrder(normalizeSortOrder(hydrated))
}

function isRowChanged(item: EventTypeRow, initial?: EventTypeRow): boolean {
  if (!initial) return true
  return (
    item.title.en !== initial.title.en ||
    item.title.he !== initial.title.he ||
    item.showInDashboard !== initial.showInDashboard ||
    item.sortOrder !== initial.sortOrder ||
    item.active !== initial.active ||
    (item.dashboardOrder ?? -1) !== (initial.dashboardOrder ?? -1)
  )
}

export default function EventTypesModal() {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { toggleEventTypesModal } = modalsSlice.actions
  const visible = useAppSelector((state) => state.modalsReducer.eventTypes)

  const { data, isFetching, refetch } = eventApi.useGetEventTypesListQuery(undefined, {
    skip: visible !== 'open',
    refetchOnMountOrArgChange: true,
  })

  useEffect(() => {
    if (visible === 'open') refetch()
  }, [visible, refetch])
  const [sendUpdateOtp, { isLoading: isSendingOtp }] = eventApi.useSendUpdateEventTypesOtpMutation()
  const [batchUpdate, { isLoading: isSaving }] = eventApi.useBatchUpdateEventTypesMutation()

  const [tab, setTab] = useState<EventTypesTab>('all')
  const [items, setItems] = useState<EventTypeRow[]>([])
  const [initialItems, setInitialItems] = useState<EventTypeRow[]>([])
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [otpModalOpen, setOtpModalOpen] = useState(false)
  const [newTitle, setNewTitle] = useState({ en: '', he: '' })
  const newIdCounter = useRef(0)

  useEffect(() => {
    if (visible === 'open' && data?.list) {
      const hydrated = hydrateInitial(data.list as Array<Partial<EventTypeRow>>)
      setItems(hydrated)
      setInitialItems(hydrated)
    }
  }, [visible, data])

  const dashboardItems = useMemo(
    () =>
      items
        .filter((x) => x.showInDashboard)
        .sort((a, b) => (a.dashboardOrder ?? Number.MAX_SAFE_INTEGER) - (b.dashboardOrder ?? Number.MAX_SAFE_INTEGER)),
    [items]
  )

  const allItemsSorted = useMemo(
    () => items.filter((x) => x.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [items]
  )

  const deletedItems = useMemo(
    () => items.filter((x) => !x.active).sort((a, b) => a.title.en.localeCompare(b.title.en)),
    [items]
  )

  const hasChanges = useMemo(() => {
    if (items.length !== initialItems.length) return true
    const byId = new Map(initialItems.map((x) => [x.id, x]))
    return items.some((item) => isRowChanged(item, byId.get(item.id)))
  }, [items, initialItems])

  const onCloseModal = () => {
    setTab('all')
    setItems([])
    setInitialItems([])
    setNewTitle({ en: '', he: '' })
    setOtpModalOpen(false)
    dispatch(toggleEventTypesModal())
  }

  const updateTitle = (id: string, lang: 'en' | 'he', value: string) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, title: { ...x.title, [lang]: value } } : x)))
  }

  const applyAllOrder = (orderedActive: EventTypeRow[]) => {
    const orderMap = new Map<string, number>()
    orderedActive.forEach((x, i) => orderMap.set(x.id, i))
    setItems((prev) => prev.map((x) => (orderMap.has(x.id) ? { ...x, sortOrder: orderMap.get(x.id) as number } : x)))
  }

  const moveAll = (id: string, direction: -1 | 1) => {
    const sorted = [...allItemsSorted]
    const index = sorted.findIndex((x) => x.id === id)
    if (index < 0) return
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= sorted.length) return
    ;[sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]]
    applyAllOrder(sorted)
  }

  const moveDashboard = (id: string, direction: -1 | 1) => {
    const dash = items
      .filter((x) => x.showInDashboard)
      .sort((a, b) => (a.dashboardOrder ?? Number.MAX_SAFE_INTEGER) - (b.dashboardOrder ?? Number.MAX_SAFE_INTEGER))
    const index = dash.findIndex((x) => x.id === id)
    if (index < 0) return
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= dash.length) return
    ;[dash[index], dash[targetIndex]] = [dash[targetIndex], dash[index]]
    const orderMap = new Map<string, number>()
    dash.forEach((x, i) => orderMap.set(x.id, i))
    setItems((prev) =>
      prev.map((x) => ({
        ...x,
        dashboardOrder: orderMap.has(x.id) ? (orderMap.get(x.id) as number) : x.dashboardOrder,
      }))
    )
  }

  const dropAllAt = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return
    const sorted = [...allItemsSorted]
    const from = sorted.findIndex((x) => x.id === sourceId)
    const to = sorted.findIndex((x) => x.id === targetId)
    if (from < 0 || to < 0) return
    const [pulled] = sorted.splice(from, 1)
    sorted.splice(to, 0, pulled)
    applyAllOrder(sorted)
  }

  const dropDashboardAt = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return
    const dash = items
      .filter((x) => x.showInDashboard)
      .sort((a, b) => (a.dashboardOrder ?? Number.MAX_SAFE_INTEGER) - (b.dashboardOrder ?? Number.MAX_SAFE_INTEGER))
    const from = dash.findIndex((x) => x.id === sourceId)
    const to = dash.findIndex((x) => x.id === targetId)
    if (from < 0 || to < 0) return
    const [pulled] = dash.splice(from, 1)
    dash.splice(to, 0, pulled)
    const orderMap = new Map<string, number>()
    dash.forEach((x, i) => orderMap.set(x.id, i))
    setItems((prev) =>
      prev.map((x) => ({
        ...x,
        dashboardOrder: orderMap.has(x.id) ? (orderMap.get(x.id) as number) : x.dashboardOrder,
      }))
    )
  }

  const toggleShowInDashboard = (id: string, nextShow: boolean) => {
    setItems((prev) => {
      const currentDash = prev
        .filter((x) => x.showInDashboard)
        .sort((a, b) => (a.dashboardOrder ?? Number.MAX_SAFE_INTEGER) - (b.dashboardOrder ?? Number.MAX_SAFE_INTEGER))
        .map((x) => x.id)

      let nextDash: string[]
      if (nextShow) {
        nextDash = [...currentDash.filter((x) => x !== id), id]
      } else {
        nextDash = currentDash.filter((x) => x !== id)
      }

      const orderMap = new Map<string, number>()
      nextDash.forEach((rowId, i) => orderMap.set(rowId, i))

      return prev.map((x) => {
        if (x.id === id) {
          return {
            ...x,
            showInDashboard: nextShow,
            dashboardOrder: nextShow ? (orderMap.get(id) as number) : null,
          }
        }
        return {
          ...x,
          dashboardOrder: orderMap.has(x.id) ? (orderMap.get(x.id) as number) : null,
        }
      })
    })
  }

  const handleAddType = () => {
    const en = newTitle.en.trim()
    const he = newTitle.he.trim()

    if (!en || !he) {
      toast.error(translate('eventTypesTitleRequiredError'))
      return
    }

    // The server builds the type's internal value from the English title.
    if (!/[a-zA-Z0-9]/.test(en)) {
      toast.error(translate('eventTypesTitleEnLatinError'))
      return
    }

    const duplicate = items.find(
      (x) =>
        x.title.en.trim().toLowerCase() === en.toLowerCase() ||
        x.value.toLowerCase() === en.toLowerCase() ||
        x.title.he.trim() === he
    )

    if (duplicate) {
      toast.error(translate(duplicate.active ? 'eventTypesDuplicateError' : 'eventTypesDuplicateDeletedError'))
      return
    }

    newIdCounter.current += 1

    const row: EventTypeRow = {
      id: `new-${newIdCounter.current}`,
      value: '',
      title: { en, he },
      showInDashboard: false,
      sortOrder: Number.MAX_SAFE_INTEGER,
      dashboardOrder: null,
      active: true,
      isNew: true,
    }

    setItems((prev) => normalizeSortOrder([...prev, row]))
    setNewTitle({ en: '', he: '' })
  }

  const handleDeleteType = (item: EventTypeRow) => {
    // A type that was never saved has nothing to keep for old events, so it is just dropped.
    if (item.isNew) {
      setItems((prev) => normalizeSortOrder(prev.filter((x) => x.id !== item.id)))
      return
    }

    const title = item.title.en || item.value
    if (!window.confirm(`${title}: ${intl.formatMessage({ id: 'eventTypesDeleteConfirm' })}`)) return

    setItems((prev) =>
      normalizeDashboardOrder(
        normalizeSortOrder(
          prev.map((x) => (x.id === item.id ? { ...x, active: false, showInDashboard: false, dashboardOrder: null } : x))
        )
      )
    )
  }

  const handleRestoreType = (id: string) => {
    setItems((prev) =>
      normalizeSortOrder(prev.map((x) => (x.id === id ? { ...x, active: true, sortOrder: Number.MAX_SAFE_INTEGER } : x)))
    )
  }

  const buildChangedPayload = () => {
    const byId = new Map(initialItems.map((x) => [x.id, x]))

    return {
      items: items
        .filter((item) => !item.isNew && isRowChanged(item, byId.get(item.id)))
        .map((item) => ({
          id: item.id,
          title: item.title,
          showInDashboard: item.showInDashboard,
          sortOrder: item.sortOrder,
          dashboardOrder: item.dashboardOrder,
          active: item.active,
        })),
      created: items
        .filter((item) => item.isNew)
        .map((item) => ({
          title: item.title,
          showInDashboard: item.showInDashboard,
          sortOrder: item.sortOrder,
          dashboardOrder: item.dashboardOrder,
        })),
    }
  }

  const handleApply = async () => {
    const payload = buildChangedPayload()
    if (!payload.items.length && !payload.created.length) return

    const res: any = await sendUpdateOtp(payload)
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    setOtpModalOpen(true)
    toast.success(translate('eventTypesOtpSent'))
  }

  const handleResendOtp = async () => {
    const payload = buildChangedPayload()
    if (!payload.items.length && !payload.created.length) return

    const res: any = await sendUpdateOtp(payload)
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    toast.success(translate('eventTypesOtpSent'))
  }

  const handleConfirmOtp = async (finalCode: string) => {
    const res: any = await batchUpdate({ code: finalCode })
    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    toast.success(translate('eventTypesApplySuccess'))
    setOtpModalOpen(false)
    setInitialItems(items)
    refetch()
  }


  const renderRow = (item: EventTypeRow, opts: { mode: 'all' | 'dashboard'; index: number; totalInMode: number }) => {
    const canUp = opts.index > 0
    const canDown = opts.index < opts.totalInMode - 1
    const isDragged = draggedId === item.id
    const isDragOver = dragOverId === item.id && draggedId && draggedId !== item.id

    return (
      <Box
        key={item.id}
        draggable={!isSaving}
        onDragStart={(e) => {
          setDraggedId(item.id)
          e.dataTransfer.effectAllowed = 'move'
          try { e.dataTransfer.setData('text/plain', item.id) } catch { /* Firefox needs data set */ }
        }}
        onDragOver={(e) => {
          if (!draggedId || draggedId === item.id) return
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          if (dragOverId !== item.id) setDragOverId(item.id)
        }}
        onDragLeave={() => {
          if (dragOverId === item.id) setDragOverId(null)
        }}
        onDrop={(e) => {
          e.preventDefault()
          if (!draggedId || draggedId === item.id) return
          if (opts.mode === 'all') dropAllAt(draggedId, item.id)
          else dropDashboardAt(draggedId, item.id)
          setDraggedId(null)
          setDragOverId(null)
        }}
        onDragEnd={() => {
          setDraggedId(null)
          setDragOverId(null)
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: isDragOver ? '2px dashed #ED6C02' : '1px solid #f0e6d8',
          borderRadius: 1,
          backgroundColor: item.showInDashboard ? '#fffaf3' : '#fafafa',
          opacity: isDragged ? 0.4 : 1,
          transition: 'opacity 0.15s, border-color 0.15s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#b0b0b0',
            cursor: isSaving ? 'default' : 'grab',
            '&:active': { cursor: isSaving ? 'default' : 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
          <IconButton
            size="small"
            color="warning"
            disabled={!canUp || isSaving}
            onClick={() => (opts.mode === 'all' ? moveAll(item.id, -1) : moveDashboard(item.id, -1))}
            aria-label="up"
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="warning"
            disabled={!canDown || isSaving}
            onClick={() => (opts.mode === 'all' ? moveAll(item.id, 1) : moveDashboard(item.id, 1))}
            aria-label="down"
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="caption" color={item.isNew ? '#ED6C02' : 'textSecondary'}>
            {item.isNew ? translate('eventTypesNew') : <code>{item.value}</code>}
          </Typography>
          <Box display="flex" gap={1}>
            <TextField
              size="small"
              variant="outlined"
              color="warning"
              label={intl.formatMessage({ id: 'eventTypesTranslationEn' })}
              value={item.title.en}
              onChange={(e) => updateTitle(item.id, 'en', e.target.value)}
              disabled={isSaving}
              InputProps={{ readOnly: opts.mode === 'dashboard' }}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              variant="outlined"
              color="warning"
              label={intl.formatMessage({ id: 'eventTypesTranslationHe' })}
              value={item.title.he}
              onChange={(e) => updateTitle(item.id, 'he', e.target.value)}
              disabled={isSaving}
              InputProps={{ readOnly: opts.mode === 'dashboard' }}
              sx={{ flex: 1 }}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5} sx={{ minWidth: 130 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption">
              {opts.mode === 'all' ? translate('eventTypesOrder') : translate('eventTypesDashboardOrder')}:
            </Typography>
            <Typography variant="caption" fontWeight={700}>
              {opts.mode === 'all'
                ? item.sortOrder + 1
                : (item.dashboardOrder !== null && item.dashboardOrder !== undefined ? item.dashboardOrder + 1 : '-')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption">{translate('eventTypesShowInDashboard')}</Typography>
            <Switch
              size="small"
              color="warning"
              checked={item.showInDashboard}
              disabled={isSaving}
              onChange={(e) => toggleShowInDashboard(item.id, e.target.checked)}
            />
          </Box>
        </Box>

        {opts.mode === 'all' && (
          <Tooltip title={translate('eventTypesDelete')}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={isSaving}
                onClick={() => handleDeleteType(item)}
                aria-label="delete"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )
  }

  const renderDeletedRow = (item: EventTypeRow) => (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: '#fafafa',
      }}
    >
      <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="caption" color="textSecondary">
          <code>{item.value}</code>
        </Typography>
        <Typography variant="body2">
          {item.title.en} / {item.title.he}
        </Typography>
      </Box>
      <Button
        size="small"
        color="warning"
        variant="outlined"
        startIcon={<RestoreFromTrashIcon fontSize="small" />}
        disabled={isSaving}
        onClick={() => handleRestoreType(item.id)}
      >
        {translate('eventTypesRestore')}
      </Button>
    </Box>
  )

  return (
    <Modal
      title={translate('eventTypesModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '760px' }}
      setVisible={onCloseModal}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          textColor="secondary"
          TabIndicatorProps={{ style: { backgroundColor: '#ED6C02' } }}
          sx={{
            '.MuiTab-root': { color: '#8b8b8b' },
            '.MuiTab-root.Mui-selected': { color: '#ED6C02' },
          }}
        >
          <Tab value="all" label={translate('eventTypesTabAll')} />
          <Tab value="dashboard" label={translate('eventTypesTabDashboard')} />
          <Tab value="deleted" label={translate('eventTypesTabDeleted')} />
        </Tabs>

        {tab === 'all' && !isFetching && (
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              variant="outlined"
              color="warning"
              label={intl.formatMessage({ id: 'eventTypesTranslationEn' })}
              value={newTitle.en}
              onChange={(e) => setNewTitle((prev) => ({ ...prev, en: e.target.value }))}
              disabled={isSaving}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              variant="outlined"
              color="warning"
              label={intl.formatMessage({ id: 'eventTypesTranslationHe' })}
              value={newTitle.he}
              onChange={(e) => setNewTitle((prev) => ({ ...prev, he: e.target.value }))}
              disabled={isSaving}
              sx={{ flex: 1 }}
            />
            <Button
              color="warning"
              variant="outlined"
              disabled={isSaving || !newTitle.en.trim() || !newTitle.he.trim()}
              onClick={handleAddType}
            >
              {translate('eventTypesAdd')}
            </Button>
          </Box>
        )}

        {isFetching ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="warning" />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1} sx={{ maxHeight: '55vh', overflowY: 'auto', pr: 1 }}>
            {tab === 'all' &&
              allItemsSorted.map((item, index) =>
                renderRow(item, { mode: 'all', index, totalInMode: allItemsSorted.length })
              )}
            {tab === 'dashboard' &&
              dashboardItems.map((item, index) =>
                renderRow(item, { mode: 'dashboard', index, totalInMode: dashboardItems.length })
              )}
            {tab === 'dashboard' && dashboardItems.length === 0 && (
              <Typography color="textSecondary" textAlign="center" py={4}>
                —
              </Typography>
            )}
            {tab === 'deleted' && deletedItems.map((item) => renderDeletedRow(item))}
            {tab === 'deleted' && deletedItems.length === 0 && (
              <Typography color="textSecondary" textAlign="center" py={4}>
                {translate('eventTypesDeletedEmpty')}
              </Typography>
            )}
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            color="warning"
            variant="contained"
            disabled={!hasChanges || isSaving || isSendingOtp}
            onClick={handleApply}
          >
            {isSendingOtp ? <CircularProgress size={20} /> : translate('eventTypesApply')}
          </Button>
        </Box>
      </Box>

      <ConfirmActionOtpModal
        visible={otpModalOpen}
        title={translate('eventTypesOtpModalTitle')}
        description={translate('eventTypesOtpModalDescription')}
        onClose={() => setOtpModalOpen(false)}
        onConfirm={handleConfirmOtp}
        onResend={handleResendOtp}
        isConfirming={isSaving}
        isResending={isSendingOtp}
      />
    </Modal>
  )
}
